import { DatePipe } from '@angular/common';
import { Component, effect, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { WEEK_OPTIONS } from '@shared/constants/date.constants';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { v4 as uuidv4 } from 'uuid';
import { ICalendarDay } from './dashboard-calendar.interface';

@Component({
  imports: [DatePipe, TranslateModule, MatButtonModule, NgxSkeletonLoaderModule, IconComponent],
  selector: 'app-dashboard-calendar',
  styleUrl: './dashboard-calendar.component.scss',
  templateUrl: './dashboard-calendar.component.html',
})
export class DashboardCalendarComponent {
  public readonly campaigns = input.required<TCampaignApi[]>();
  public readonly selectedDate = input.required<Date>();
  public readonly selectedMonth = input.required<Date>();

  public readonly loadingCampaigns = input<boolean>(false);

  public readonly dateSelected = output<Date>();
  public readonly monthChanged = output<Date>();

  public readonly daysOfWeek: string[] = [];
  public readonly calendarDays = signal<ICalendarDay[]>([]);

  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);

  constructor() {
    this.initializeDaysOfWeek();

    effect(() => {
      this.regenerateCalendar();
    });
  }

  public onPreviousMonth(): void {
    const previousMonth = new Date(this.selectedMonth().getFullYear(), this.selectedMonth().getMonth() - 1, 1);
    this.monthChanged.emit(previousMonth);
    this.dateSelected.emit(isSameMonth(previousMonth, new Date()) ? new Date() : startOfMonth(previousMonth));
  }

  public onNextMonth(): void {
    const nextMonth = new Date(this.selectedMonth().getFullYear(), this.selectedMonth().getMonth() + 1, 1);
    this.monthChanged.emit(nextMonth);
    this.dateSelected.emit(isSameMonth(nextMonth, new Date()) ? new Date() : startOfMonth(nextMonth));
  }

  public selectDay(date: Date): void {
    if (!isSameMonth(date, this.selectedMonth())) {
      return;
    }

    this.dateSelected.emit(date);
  }

  public isCurrentMonth(date: Date): boolean {
    return isSameMonth(date, this.selectedMonth());
  }

  public isCurrentDay(day: Date): boolean {
    return isSameDay(day, new Date());
  }

  public isSelectedDay(day: Date): boolean {
    return isSameDay(day, this.selectedDate());
  }

  public hasActiveCampaign(day: Date): boolean {
    return this.campaigns().some((campaign) => {
      if (!campaign.start || !campaign.end) {
        return false;
      }

      return isWithinInterval(day, { end: campaign.end, start: campaign.start });
    });
  }

  public getMonthYearLabel(): string {
    return this.dateUtils.formatDate(this.selectedMonth(), 'MMMM yyyy');
  }

  private initializeDaysOfWeek(): void {
    this.daysOfWeek.push(
      ...['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) =>
        this.translate.instant(`shared.days.short.${day}`),
      ),
    );
  }

  private regenerateCalendar(): void {
    const monthStart = startOfMonth(this.selectedMonth());
    const monthEnd = endOfMonth(this.selectedMonth());
    const calendarStart = startOfWeek(monthStart, WEEK_OPTIONS);
    const calendarEnd = endOfWeek(monthEnd, WEEK_OPTIONS);
    const days = eachDayOfInterval({ end: calendarEnd, start: calendarStart });

    this.calendarDays.set(
      days.map((day) => ({
        date: day,
        hasActiveCampaign: this.loadingCampaigns() ? true : this.hasActiveCampaign(day),
        id: uuidv4(),
        isToday: isSameDay(day, new Date()),
      })),
    );
  }
}
